import { useCallback } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useAddToast } from '../../../contexts/ToastContext';
import { useAsyncValue, useRequestConnectedToAsyncValue } from '../../../hooks';
import { LoaderButton } from '../../generic/Button';
import useIssueOverview from '../hooks/useIssueOverview';
import DateFilter from './DateFilter';
import IssueFilter from './IssueFilter';
import LabelFilter from './LabelFilter';
import type { Category } from '../../../../shared/issuesV2';
import type { AsyncTaskResponse } from '../../../../shared/types/routes/common';
import { triggerExport } from '../api';

import styles from '../IssueOverview.module.less';
import { useAddJobToNotificationQueue } from '../../JobNotificationHub/Provider';

type Props = {
  isDisabled: boolean;
};

/**
 * Container component for all of the filter buttons on the Issue Overview page.
 */
const FilterSection: VoidFunctionComponent<Props> = ({ isDisabled }) => {
  const addToast = useAddToast();
  const { filters, setFilters, isProjectPage } = useIssueOverview();
  const value = useAsyncValue<AsyncTaskResponse>();
  const addJob = useAddJobToNotificationQueue();

  const exportAndShowToast = useCallback(async () => {
    const { data } = await triggerExport(filters);
    if (!data || !data.jobToken || !data.task) {
      throw new Error('Failed to submit task.');
    }

    addJob(data.jobToken, data.task);
    addToast({ type: 'success', content: 'Issue Overview export queued. The report will be emailed shortly.' });
    return { data };
  }, [filters, addToast, addJob]);

  const onExport = useRequestConnectedToAsyncValue(exportAndShowToast, value);

  const setLabels = (labels: number[]) =>
    setFilters({ ...filters, labels: labels.length > 0 ? labels : undefined });

  const setDates = (start: Date, end: Date) =>
    setFilters({ ...filters, start, end });

  const setCategory = (category: Category | undefined) => {
    setFilters({ ...filters, category });
  };

  return (
    <div className={styles.filterButtonsContainer}>
      <IssueFilter category={filters.category} setCategory={setCategory} isDisabled={isDisabled || value.isLoading} />
      {!isProjectPage && (
        <LabelFilter filters={filters} setLabels={setLabels} isDisabled={isDisabled || value.isLoading} />
      )}
      <DateFilter filters={filters} setDates={setDates} isDisabled={isDisabled || value.isLoading} />
      <LoaderButton isLoading={value.isLoading} onClick={onExport} isDisabled={isDisabled || value.isLoading}>
        Export
      </LoaderButton>
    </div>
  );
};

export default FilterSection;
